import { CheckCircle, ImageNotSupported, Search } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { Media } from '../../Model/MediaModel'

interface Props {
  open: boolean
  medias: Media[]
  onClose: () => void
  onConfirm: (ids: number[]) => void
}

export default function MediaPickerDialog({ open, medias, onClose, onConfirm }: Props) {
  const theme = useTheme()
  const [selected, setSelected] = useState<number[]>([])
  const [search, setSearch] = useState('')

  const filtered = medias.filter(m =>
    m.desc.toLowerCase().includes(search.toLowerCase())
  )

  function toggle(id: number) {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  function handleConfirm() {
    onConfirm(selected)
    reset()
  }

  function handleClose() {
    reset()
    onClose()
  }

  function reset() {
    setSelected([])
    setSearch('')
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3">Selecionar Mídia</Typography>
          {selected.length > 0 && (
            <Typography variant="body2" color="primary">
              {selected.length} selecionado{selected.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField fullWidth size="small" placeholder="Buscar por nome..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />

        {filtered.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1, color: 'text.disabled' }}>
            <ImageNotSupported sx={{ fontSize: 48 }} />
            <Typography variant="body2">Nenhuma mídia encontrada</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
            {filtered.map(media => {
              const isSelected = selected.includes(media.id)
              return (
                <Box key={media.id} onClick={() => toggle(media.id)} sx={{
                  cursor: 'pointer', borderRadius: 2, overflow: 'hidden', position: 'relative',
                  border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: theme.palette.primary.light },
                }}>
                  <Box sx={{ height: 100, overflow: 'hidden', bgcolor: 'grey.100' }}>
                    <Box component="img" src={media.urlPreview} alt={media.desc}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </Box>
                  <Typography variant="caption" noWrap sx={{ px: 1, py: 0.5, display: 'block', color: 'text.secondary' }}>
                    {media.desc}
                  </Typography>
                  {isSelected && (
                    <CheckCircle color="primary" sx={{
                      position: 'absolute', top: 6, right: 6,
                      bgcolor: 'background.paper', borderRadius: '50%', fontSize: 20
                    }} />
                  )}
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} color="inherit">Cancelar</Button>
        <Button variant="contained" disabled={!selected.length} onClick={handleConfirm}>
          {selected.length > 0 ? `Adicionar (${selected.length})` : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
